import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const InviteUserModal = ({ isOpen, onClose, onInviteSent }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    role: 'user',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  const roleOptions = [
    { value: 'global_administrator', label: 'Global Administrator', description: 'Highest-level access, same as Administrator' },
    { value: 'administrator', label: 'Administrator', description: 'Full system access' },
    { value: 'user', label: 'User', description: 'Standard access' },
    { value: 'view_only', label: 'View Only', description: 'Read-only access' }
  ];

  const validateField = (field, value) => {
    switch (field) {
      case 'email':
        if (!value?.trim()) return 'Email address is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) return 'Enter a valid email address (e.g. name@company.com)';
        return '';
      case 'role':
        if (!value) return 'Please select a role for this team member';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    ['email', 'role']?.forEach(field => {
      const error = validateField(field, formData?.[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    setTouched({ email: true, role: true });
    return Object.keys(newErrors)?.length === 0;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData?.[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const expiresAt = new Date();
      expiresAt?.setDate(expiresAt?.getDate() + 7);

      const invitationToken = await generateToken();

      const { error } = await supabase?.from('invitations')?.insert({
          email: formData?.email?.toLowerCase()?.trim(),
          role: formData?.role,
          invited_by: userProfile?.id || null,
          invitation_token: invitationToken,
          expires_at: expiresAt?.toISOString(),
          status: 'pending'
        });

      if (error) {
        if (error?.code === '42501' || error?.message?.includes('permission denied') || error?.message?.includes('new row violates')) {
          throw new Error('Only administrators can send invitations. Please contact your system administrator.');
        }
        throw error;
      }

      const APP_URL = 'https://constructi6448.builtwithrocket.new';
      const invitationUrl = `${APP_URL}/invitation-acceptance?token=${invitationToken}`;

      // Send invitation email via edge function
      let emailResult = null;
      try {
        const { data } = await supabase?.functions?.invoke('send-invitation-email', {
          body: {
            email: formData?.email?.toLowerCase()?.trim(),
            role: formData?.role,
            inviterName: userProfile?.full_name || 'A team administrator',
            invitationToken,
            message: formData?.message?.trim() || null,
          },
        });
        emailResult = data;
      } catch (emailErr) {
        console.warn('Email send failed (non-blocking):', emailErr?.message);
      }

      const emailSent = emailResult?.method && emailResult?.method !== 'db_only';
      setSuccessInfo({
        email: formData?.email?.toLowerCase()?.trim(),
        invitationUrl,
        emailSent,
        warning: emailResult?.warning || null,
      });

      setFormData({ email: '', role: 'user', message: '' });
      setErrors({});
      setTouched({});
      onInviteSent();
    } catch (error) {
      console.error('Error sending invitation:', error?.message);
      setErrors({ submit: error?.message || 'Failed to send invitation' });
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte?.toString(16)?.padStart(2, '0'))?.join('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched?.[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const getRoleDescription = (roleValue) => {
    const descriptions = {
      administrator: 'Can manage users, settings, and all project data',
      user: 'Can view and edit projects, bids, and resources',
      view_only: 'Can only view data — no editing or creating'
    };
    return descriptions?.[roleValue] || '';
  };

  if (!isOpen) return null;

  if (successInfo) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
        <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg">
          <div className="border-b border-border px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Invitation Sent!</h2>
                <p className="text-xs text-muted-foreground mt-0.5">The invitation has been created</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { setSuccessInfo(null); onClose(); }}>
              <Icon name="X" size={20} />
            </Button>
          </div>
          <div className="p-6 space-y-4">
            {successInfo?.emailSent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="Mail" size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">Email sent successfully</p>
                  <p className="text-xs text-green-700 mt-1">An invitation email was sent to <strong>{successInfo?.email}</strong>.</p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Invitation saved — share the link manually</p>
                  <p className="text-xs text-amber-700 mt-1">The invitation was created but the email could not be sent automatically. Please share the link below with <strong>{successInfo?.email}</strong>.</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Invitation Link</p>
              <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-lg p-3">
                <p className="text-xs text-foreground break-all flex-1 font-mono">{successInfo?.invitationUrl}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigator.clipboard?.writeText(successInfo?.invitationUrl)}
                  className="flex-shrink-0"
                >
                  <Icon name="Copy" size={14} />
                </Button>
              </div>
            </div>
            <Button
              variant="default"
              className="w-full"
              onClick={() => { setSuccessInfo(null); onClose(); }}
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Invite Team Member</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Fields marked with <span className="text-destructive font-medium">*</span> are required</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5" noValidate>
          {/* Email Field */}
          <div className="space-y-1.5">
            <Input
              label="Email Address"
              type="email"
              placeholder="e.g. john.smith@yourcompany.com"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              onBlur={() => handleBlur('email')}
              error={touched?.email ? errors?.email : ''}
              required
              description={!touched?.email || !errors?.email ? "Enter the recipient's work email address" : undefined}
            />
            {touched?.email && !errors?.email && formData?.email && (
              <div className="flex items-center gap-1.5 mt-1">
                <Icon name="CheckCircle" size={14} className="text-green-600" />
                <span className="text-xs text-green-600">Valid email address</span>
              </div>
            )}
          </div>

          {/* Role Field */}
          <div className="space-y-1.5">
            <Select
              label="Role"
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => handleChange('role', value)}
              onBlur={() => handleBlur('role')}
              error={touched?.role ? errors?.role : ''}
              description={formData?.role && !errors?.role ? getRoleDescription(formData?.role) : 'Select the access level for this team member'}
              required
            />
          </div>

          {/* Role Permission Preview */}
          {formData?.role && (
            <div className="bg-muted/40 border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Shield" size={14} className="text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role Permissions Preview</span>
              </div>
              <div className="grid grid-cols-1 gap-1">
                {formData?.role === 'administrator' && [
                  'Full system access', 'Manage users & invitations', 'Configure system settings'
                ]?.map(p => (
                  <div key={p} className="flex items-center gap-1.5">
                    <Icon name="Check" size={12} className="text-green-600" />
                    <span className="text-xs text-foreground">{p}</span>
                  </div>
                ))}
                {formData?.role === 'user' && [
                  'View & manage projects and bids', 'Manage resources', 'View analytics'
                ]?.map(p => (
                  <div key={p} className="flex items-center gap-1.5">
                    <Icon name="Check" size={12} className="text-green-600" />
                    <span className="text-xs text-foreground">{p}</span>
                  </div>
                ))}
                {formData?.role === 'view_only' && [
                  'View dashboard & projects', 'View customers & bids', 'No editing permissions'
                ]?.map(p => (
                  <div key={p} className="flex items-center gap-1.5">
                    <Icon name="Eye" size={12} className="text-blue-500" />
                    <span className="text-xs text-foreground">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Welcome Message */}
          <Input
            label="Welcome Message"
            type="text"
            placeholder="e.g. Welcome to the team! Looking forward to working with you."
            value={formData?.message}
            onChange={(e) => handleChange('message', e?.target?.value)}
            description="Optional — add a personal note to the invitation email"
          />

          {errors?.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{errors?.submit}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
              <p className="text-xs text-blue-700">
                An invitation email will be sent to the recipient. The invitation will expire in <strong>7 days</strong>.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Send"
              iconPosition="left"
              className="flex-1"
              loading={loading}
              disabled={loading}
            >
              Send Invitation
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;